import { useEffect, useId, useRef, type JSX, type ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import { Button } from './Button.js';
import './Sheet.css';

/**
 * Bottom sheet for buy-in and call prompts. Focus is trapped while it is open:
 * a call prompt that lets focus wander back to the table is how a player
 * mis-taps a chip decision.
 */

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface SheetProps {
  open: boolean;
  onClose(): void;
  title: string;
  children: ReactNode;
  /** Right-aligned action row; the primary action comes last. */
  actions?: ReactNode;
  /** Compliance line — required on every chip-adjacent sheet (design spec §13). */
  footnote?: string;
  /** Call prompts with a running timer must not be dismissable by a stray tap. */
  dismissable?: boolean;
  className?: string;
}

export function Sheet({
  open,
  onClose,
  title,
  children,
  actions,
  footnote,
  dismissable = true,
  className,
}: SheetProps): JSX.Element | null {
  const t = useT();
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();

  /**
   * Callers pass `onClose` as an inline lambda, so its identity changes on every
   * parent render. Reading it through a ref keeps the trap effect keyed on
   * `open` alone — otherwise each parent render tore the trap down and rebuilt
   * it, yanking focus back to the first control mid-interaction.
   */
  const latest = useRef({ onClose, dismissable });
  latest.current = { onClose, dismissable };

  useEffect(() => {
    if (!open) return;
    const returnTo = document.activeElement;
    const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel.current)?.focus();

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape' && latest.current.dismissable) {
        e.stopPropagation();
        latest.current.onClose();
        return;
      }
      if (e.key !== 'Tab' || panel.current === null) return;
      const stops = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      const edge = e.shiftKey ? stops[0] : stops[stops.length - 1];
      if (stops.length === 0 || document.activeElement === edge) {
        e.preventDefault();
        (e.shiftKey ? stops[stops.length - 1] : stops[0])?.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (returnTo instanceof HTMLElement) returnTo.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div
        className="sheet-scrim"
        onClick={dismissable ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        className={className === undefined ? 'sheet' : `sheet ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panel}
        tabIndex={-1}
      >
        <div className="sheet__grabber" aria-hidden="true" />
        <div className="sheet__head">
          <h2 className="sheet__title" id={titleId}>
            {title}
          </h2>
          {dismissable ? (
            <Button variant="ghost" onClick={onClose} aria-label={t('ui.sheet.close.aria')}>
              {t('ui.sheet.close')}
            </Button>
          ) : null}
        </div>
        <div className="sheet__body scroll-y">{children}</div>
        {actions === undefined ? null : <div className="sheet__actions">{actions}</div>}
        {footnote === undefined ? null : <p className="sheet__footnote">{footnote}</p>}
      </div>
    </>
  );
}
